import { Link } from 'react-router-dom';
import { DocSection } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'model-families', label: 'The model families' },
  { id: 'vram-batch', label: 'VRAM & batch sizing' },
  { id: 'weak-card', label: 'What degrades on a weak card' },
  { id: 'managing-models', label: 'Managing models & downloads' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          ClipRadar does its work on your GPU. Analysis (the Visual, Speech, and Faces passes) and
          Ultra Search both run local AI models that need an <strong>NVIDIA GPU with CUDA</strong> —
          there's no supported CPU mode. What your GPU actually buys you is VRAM: how much of it is
          free right now decides how fast analysis runs, which models are available, and whether
          Ultra Search can turn on at all.
        </p>
        <DocShot
          slug="models-and-your-gpu"
          shot="settings-models"
          alt="Settings → Models section"
          caption="Settings → Models: what's downloaded, and the controls for trading accuracy for speed."
          callouts={[
            { n: 1, label: 'Visual model — fixed, not user-selectable' },
            { n: 2, label: 'Speech model — pick a smaller size for a tighter GPU' },
            { n: 3, label: 'Download status per model — Downloaded / Downloading / Failed, with Retry' },
            { n: 4, label: 'Check transcripts — a one-off scan, not an analysis job' },
          ]}
        />
      </DocSection>

      <DocSection id="model-families" title="The model families">
        <p>ClipRadar downloads and runs four separate model families, each doing one job:</p>
        <ul>
          <li>
            <strong>Visual</strong> — the OpenCLIP model that embeds every frame, powering Visuals
            search and scene detection. Fixed; not user-selectable.
          </li>
          <li>
            <strong>Speech</strong> — faster-whisper transcribes every source; pick a size from
            Large v3 Turbo (the default) down to Base to trade accuracy for speed on a tighter GPU.
          </li>
          <li>
            <strong>Faces</strong> — InsightFace detects and embeds every face, feeding the People
            tab's clustering.
          </li>
          <li>
            <strong>Ultra query parser</strong> — the small language model behind Ultra Search, in a
            regular and a Lite size.
          </li>
        </ul>
        <p>
          The Visual, Speech, and Faces models download on first run; the Ultra query parser is a
          separate one-time download you trigger from{' '}
          <Link to="/docs/interface/settings">Settings → Ultra Search</Link> — see{' '}
          <Link to="/docs/reference/models">Machine-learning models</Link> for the exact model
          and download size behind each one.
        </p>
      </DocSection>

      <DocSection id="vram-batch" title="VRAM & batch sizing">
        <p>
          Before each Visual analysis pass, ClipRadar checks how much VRAM is actually free right
          now — not your card's total — because you're often running Premiere or After Effects
          alongside it, and it sizes its frame batches to fit inside that free amount. Ultra Search's
          memory footprint responds to the same live reading rather than a fixed assumption about
          your card's tier.
        </p>
        <p>
          For the exact recommended and minimum VRAM figures, see{' '}
          <Link to="/docs/reference/system-requirements">System requirements</Link>.
        </p>
        <DocShot
          slug="models-and-your-gpu"
          shot="settings-ultra"
          alt="Settings → Ultra with the VRAM readout and tier note"
          caption="Settings → Ultra Search shows live VRAM and which tier Ultra is running in."
          callouts={[
            { n: 1, label: 'Query parser model — size and download state' },
            { n: 2, label: 'GPU memory policy — Automatic, Keep ready, or Load on demand' },
            { n: 3, label: "Live VRAM readout — what's free right now, and Ultra's current tier" },
            { n: 4, label: 'Advanced — point Ultra at your own OpenAI-compatible endpoint instead' },
          ]}
        />
      </DocSection>

      <DocSection id="weak-card" title="What degrades on a weak card">
        <p>
          A weaker GPU doesn't stop ClipRadar working — it changes how much VRAM headroom you have,
          and a few things respond to that headroom:
        </p>
        <ul>
          <li>
            <strong>Spill warning.</strong> If even the smallest safe batch won't fit in free VRAM,
            Windows silently spills the work into system RAM instead of erroring — roughly ten times
            slower. ClipRadar detects this ahead of time and warns you, rather than letting a pass
            crawl with no explanation.
          </li>
          <li>
            <strong>Ultra Lite / fallback.</strong> See{' '}
            <Link to="/docs/concepts/ultra-search#vram">Ultra needs VRAM</Link> — Ultra drops to a
            lighter parser model on tighter cards, and falls back to plain search, with a visible
            notice, when there truly isn't room to run it.
          </li>
          <li>
            <strong>Speech model tiering.</strong> The default Speech model is always Large v3
            Turbo. For Bulgarian audio specifically, ClipRadar can step up to the full-accuracy
            Large v3 model instead — but only if you've already downloaded it and your GPU has
            enough free VRAM to run it comfortably; it's never downloaded automatically.
          </li>
        </ul>
        <p>
          In every case, the weak-hardware story is about VRAM headroom, not about whether you have
          a GPU at all — a GPU is required either way.
        </p>
        <DocShot
          slug="models-and-your-gpu"
          shot="spill-warning"
          alt="The low-VRAM spill warning banner"
          caption="ClipRadar warns you before a low-VRAM spill turns a fast pass into a slow one."
        />
      </DocSection>

      <DocSection id="managing-models" title="Managing models & downloads">
        <p>
          Every model lives under <Link to="/docs/interface/settings">Settings</Link> — the{' '}
          <strong>Models</strong> group for downloads and the Speech model picker, the{' '}
          <strong>Ultra Search</strong> group for the query parser model and how it manages GPU
          memory between searches. Both groups are read-only inside the Premiere panel; change them
          from the desktop app.
        </p>
      </DocSection>
    </>
  );
}
